import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { BookOpen, Play, FileText, X, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function fileUrl(id, kind) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("udi_token") || "";
  return `${base}/api/ebooks/${id}/file/${kind}?auth=${encodeURIComponent(token)}`;
}

export default function EbookGallery() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null); // { book, kind }

  useEffect(() => {
    if (user && !user.activated) return;
    (async () => {
      try {
        const { data } = await api.get("/ebooks");
        setItems(data.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (user && !user.activated) {
      toast.error("Activate your account by purchasing a product to unlock the E-Book Gallery.");
    }
  }, [user]);

  if (user && !user.activated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-5 text-slate-900">
        <section className="bg-white rounded-2xl shadow-lg p-6">
          <h1
            className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <BookOpen size={26} className="text-red-700" /> E-Book Gallery
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Curated e-books, guides &amp; videos. Tap any card to read or watch.
          </p>
        </section>

        {loading ? (
          <div className="py-16 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-white/40 border-t-white animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <section
            data-testid="ebooks-empty"
            className="bg-white rounded-2xl shadow-lg p-8 text-center text-slate-600"
          >
            No e-books available yet. Please check back soon.
          </section>
        ) : (
          <section
            data-testid="ebooks-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {items.map((b) => (
              <EbookCard
                key={b.id}
                book={b}
                onOpen={(kind) => setViewing({ book: b, kind })}
              />
            ))}
          </section>
        )}
      </main>

      {viewing && (
        <ViewerModal
          book={viewing.book}
          kind={viewing.kind}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}


function EbookCard({ book, onOpen }) {
  const coverSrc = book.cover_path ? fileUrl(book.id, "cover") : null;
  return (
    <article
      data-testid={`ebook-card-${book.id}`}
      className="bg-white rounded-2xl overflow-hidden shadow-lg flex flex-col hover:-translate-y-0.5 hover:shadow-xl transition"
    >
      <div className="aspect-[4/5] bg-slate-100 relative">
        {coverSrc ? (
          <img
            src={coverSrc}
            alt={book.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <BookOpen size={48} />
          </div>
        )}
        {book.mrp > 0 && (
          <span className="absolute top-2 right-2 bg-red-700 text-white text-[11px] font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-full shadow">
            MRP {INR(book.mrp)}
          </span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3
          className="font-extrabold text-slate-900 text-base leading-snug line-clamp-2"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {book.title}
        </h3>
        {book.description && (
          <p className="mt-1 text-xs text-slate-600 line-clamp-3">{book.description}</p>
        )}
        <div className="mt-3 flex items-center gap-2">
          {book.pdf_path && (
            <a
              href={fileUrl(book.id, "pdf")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`open-pdf-${book.id}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-700 hover:bg-red-800 text-white text-xs font-bold py-2"
            >
              <FileText size={14} /> Read PDF
            </a>
          )}
          {book.video_path && (
            <button
              data-testid={`open-video-${book.id}`}
              onClick={() => onOpen("video")}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 hover:bg-black text-white text-xs font-bold py-2"
            >
              <Play size={14} /> Watch
            </button>
          )}
        </div>
      </div>
    </article>
  );
}


function ViewerModal({ book, kind, onClose }) {
  const src = useMemo(() => fileUrl(book.id, kind), [book.id, kind]);
  return (
    <div
      data-testid="ebook-viewer"
      className="fixed inset-0 z-50 bg-black/85 flex flex-col"
      onClick={onClose}
    >
      <div className="flex items-center justify-between p-3 text-white">
        <div className="text-sm font-semibold truncate">
          {book.title}
          <span className="ml-2 text-white/60 text-xs uppercase">{kind}</span>
        </div>
        <button
          onClick={onClose}
          data-testid="ebook-viewer-close"
          className="p-2 hover:bg-white/10 rounded text-white"
        >
          <X size={18} />
        </button>
      </div>
      <div
        className="flex-1 overflow-hidden bg-black"
        onClick={(e) => e.stopPropagation()}
      >
        {kind === "video" ? (
          <video
            src={src}
            controls
            autoPlay
            playsInline
            className="w-full h-full bg-black"
          />
        ) : (
          <img src={src} alt={book.title} className="w-full h-full object-contain" />
        )}
      </div>
    </div>
  );
}
